import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useTranslation } from 'react-i18next';

export default function About() {
  const { t } = useTranslation('common');
  
  const stats = [
    { number: "20+", label: t('about.experience') },
    { number: "16", label: t('about.marketsServed') },
    { number: "100K+", label: t('about.customers') },
    { number: "24/7", label: t('about.supportAvailable') }
  ];

  const milestones = [
    {
      year: "2004",
      title: t('about.year2004'),
      description: t('about.year2004Desc')
    },
    {
      year: "2008",
      title: t('about.year2008'),
      description: t('about.year2008Desc')
    },
    {
      year: "2015",
      title: t('about.year2015'),
      description: t('about.year2015Desc')
    },
    {
      year: "2024",
      title: t('about.year2024'),
      description: t('about.year2024Desc')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/5458ee659e5fe4f1c1ebb0a9d02cad77.jpg"
            alt="Our facility in Ashgabat"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-blue-900/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">{t('about.title')}</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              {t('about.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">{t('about.ourStory')}</h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {t('about.storyP1')}
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('about.storyP2')}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t('about.storyP3')}
              </p>
              <button className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                {t('about.contactUsToday')}
              </button>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20truck%20spare%20parts%20inventory%20warehouse%20interior%20in%20Turkmenistan%2C%20organized%20shelves%20with%20automotive%20parts%2C%20professional%20logistics%20facility%2C%20clean%20industrial%20environment%20with%20proper%20lighting&width=600&height=500&seq=warehouse-interior&orientation=portrait"
                alt="Our warehouse operations"
                className="rounded-xl shadow-2xl object-cover object-top w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">{t('about.ourJourney')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.journeySubtitle')}
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200"></div>
              
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                  <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-100">
                      <div className="text-2xl font-bold text-blue-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">{milestone.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20truck%20maintenance%20and%20repair%20facility%20in%20Turkmenistan%2C%20mechanics%20working%20on%20heavy%20duty%20trucks%2C%20modern%20automotive%20service%20center%2C%20reliable%20spare%20parts%20installation&width=600&height=400&seq=service-facility&orientation=landscape"
                alt="Professional service"
                className="rounded-xl shadow-2xl object-cover object-top w-full h-80"
              />
            </div>
            
            <div>
              <h2 className="text-4xl font-bold mb-6">{t('about.ourMission')}</h2>
              <p className="text-xl text-blue-100 mb-6 leading-relaxed">
                {t('about.missionDesc')}
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <i className="ri-check-line text-2xl text-blue-400 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{t('about.qualityAssurance')}</h3>
                    <p className="text-blue-100">{t('about.qualityAssuranceDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <i className="ri-truck-line text-2xl text-blue-400 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{t('about.fastLogistics')}</h3>
                    <p className="text-blue-100">{t('about.fastLogisticsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <i className="ri-customer-service-2-line text-2xl text-blue-400 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{t('about.expertSupport')}</h3>
                    <p className="text-blue-100">{t('about.expertSupportDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">{t('about.ourLocations')}</h2>
            <p className="text-xl text-gray-600">
              {t('about.locationsSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">{t('about.mainHeadquarters')}</h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-4">
                  <i className="ri-map-pin-line text-2xl text-blue-600 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('contact.address')}</h4>
                    <p className="text-gray-600">"Sahra" Building G.Kulyyev Street</p>
                    <p className="text-gray-600">Ashgabat, Turkmenistan</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <i className="ri-phone-line text-2xl text-blue-600 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('about.phone')}</h4>
                    <p className="text-gray-600">+993 65049474</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <i className="ri-time-line text-2xl text-blue-600 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t('contact.hours')}</h4>
                    <p className="text-gray-600">Every Day: 9:00 AM - 7:00 PM</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">{t('about.marketsWeServe')}</h4>
                <p className="text-gray-600 leading-relaxed">
                  {t('about.marketsDesc')}
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gray-200 rounded-xl h-96 flex items-center justify-center">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3149.0847654321!2d58.3833333!3d37.9500000!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3f7000372e1d6d6d%3A0x1234567890abcdef!2sSahra%20Building%2C%20G.Kulyyev%20Street%2C%20Ashgabat%2C%20Turkmenistan!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                  width="100%" 
                  height="100%" 
                  style={{ border: 0, borderRadius: '0.75rem' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="GB Group Location - Sahra Building, Ashgabat"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}